/**
 * Phone number display helpers.
 *
 * Numbers are stored internationally (+972XXXXXXXXX) but sometimes arrive as
 * "972503337372", "_972503337372", "+972-50-333-7372", etc. For display we want
 * the local Israeli format with a single dash after the prefix: "050-3337372".
 */

/**
 * Format any raw Israeli phone value to local display form "0XX-XXXXXXX".
 * Falls back to the digit string (or the raw input) when it can't be parsed.
 * @param {string|number} raw
 * @returns {string}
 */
export function formatPhoneIL(raw) {
  if (raw === null || raw === undefined) return "";
  let digits = String(raw).replace(/\D/g, ""); // strip +, _, spaces, dashes, letters

  // Normalize country code 972 -> local leading 0
  if (digits.startsWith("972")) {
    digits = "0" + digits.slice(3);
  }

  // Standard 10-digit local number: 0XX-XXXXXXX
  if (digits.length === 10 && digits.startsWith("0")) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  // 9 digits missing the leading 0 (e.g. "503337372") -> 0XX-XXXXXXX
  if (digits.length === 9) {
    return `0${digits.slice(0, 2)}-${digits.slice(2)}`;
  }

  return digits || String(raw);
}

/**
 * Normalize any raw Israeli phone value to the local storage form used on
 * patient docs: "0XXXXXXXXX" (10 digits, no dashes). Returns the digit string
 * unchanged when it isn't a recognizable Israeli number (never throws).
 * @param {string|number} raw
 * @returns {string}
 */
export function toLocalPhoneIL(raw) {
  if (raw === null || raw === undefined) return "";
  let digits = String(raw).replace(/\D/g, "");
  if (digits.startsWith("972")) digits = "0" + digits.slice(3);
  if (digits.length === 9 && !digits.startsWith("0")) digits = "0" + digits;
  return digits;
}

/**
 * Does a stored patient phone match a free-text search query?
 * Compares digits only (query "050-123", "+972 50 123" and "050123" all match
 * a stored "0501234567"). Returns false when the query has fewer than 2 digits
 * so a lone "0" in a name search doesn't match every patient.
 * @param {string|number} storedPhone
 * @param {string} query
 * @returns {boolean}
 */
export function phoneMatchesQuery(storedPhone, query) {
  if (!storedPhone || !query) return false;
  const q = toLocalPhoneIL(query);
  if (q.length < 2) return false;
  return toLocalPhoneIL(storedPhone).includes(q);
}
